// #3797 — FreezeWatchdog boot-log classifier.
//
// FreezeWatchdog reports the PREVIOUS launch's boot log as
// 'FreezeWatchdog:BootLog' whenever that launch never called bootComplete()
// (which only happens at the very end of a successful boot — the report is
// boot-diagnostics-reporters.service.ts reportPreviousFreezeIfAny, called from
// app.component.ts markAppLoadingAsync). It has no
// real freeze detection. On Android the dominant cause is a benign interrupted
// boot: the user backgrounds the app during a slow cold start, or Android kills
// the backgrounded process mid-boot, so the orphaned log is reported as a
// "freeze" on the next (successful) launch. Those false positives drown out the
// genuine foreground hangs (auth retry storm, WebView renderer-gone, JS crash),
// which is why FreezeWatchdog:BootLog is Android-dominated and undiagnosable.
//
// #5179 UPDATE — that "Android-dominated" reading held only while the rules
// below could fire on Android alone. Every native marker they key on was
// Android-only, so backgrounded iOS boots piled up in unknown-incomplete and
// iOS became the larger half of the report. #5179 gave iOS its own backgrounded
// marker; webview-no-js and pre-webview are still Android-only, because they key
// on markers iOS does not write (#5360). The measurements are in
// design-docs/telemetry.md.
//
// This module holds the pure decision logic so it can run under node for unit
// tests (same pattern as splash-watchdog.ts — neither the service nor the root
// component can be loaded under node). reportPreviousFreezeIfAny() in
// client/src/services/boot-diagnostics-reporters.service.ts delegates to
// classifyBootFreeze(); source-text tripwires in boot-freeze-classifier.test.ts
// enforce that the delegation stays in place, and that app.component.ts still
// calls it.

/**
 * A repeated Firebase accounts:lookup this many times or more during a boot
 * that never completed is treated as an auth retry storm (best-effort signal).
 */
export const AUTH_LOOP_MIN_OCCURRENCES = 10;

/**
 * This many page loads inside ONE app launch, with the boot never completing, is
 * a reload storm rather than a boot (#4942).
 *
 * A healthy launch loads its page once. A launch that legitimately reloads does
 * so a couple of times at most — a service-worker swap, one crash recovery. Ten
 * is far outside both and matches the threshold the auth-loop rule above already
 * uses for the same kind of judgement. The real reports run much higher: the
 * logs behind #4942 held 22, 26, 51 and 85.
 */
export const RELOAD_LOOP_MIN_DOCUMENT_LOADS = 10;

/**
 * The lines that mark the start of one loaded page, so counting them counts page
 * loads (#4942).
 *
 * `client/src/index.html` prints exactly one of these from a window `load`
 * listener, choosing between them on whether the browser has a ServiceWorker
 * API at all — NOT on whether a worker is registered. That distinction is why
 * both are needed: iOS serves the app from the `capacitor://` scheme, where
 * WKWebView exposes no such API, so it prints the first; Android serves from
 * `https://localhost`, where the Chromium WebView does expose one, so it prints
 * the second. Counting only the first would make this rule iOS-only, on a report
 * this module's own header says is Android-dominated.
 *
 * Both are logged at a level a production build keeps (`error` and `info`).
 * That matters more than it looks: a production build drops `console.log`
 * before it reaches the real console the native FreezeWatchdog capture hooks
 * (`allowedInProd: false` in logging.service.ts), so most of the app's own boot
 * lines are absent from a prod boot log and cannot be counted. These two
 * survive. The Android one was `console.log` until #4942 and left Android boot
 * logs with no page-load marker at all.
 *
 * MIRRORED TEXT: both messages are written out in `client/src/index.html`.
 * `boot-freeze-classifier.test.ts` has a tripwire that reads that file and fails
 * when a message here is not found in it — without which a rename there would
 * leave this rule counting zero for ever with every test still green.
 */
const DOCUMENT_LOAD_MARKERS = [
  'SW: ServiceWorker not supported',
  'SW: Skipping ServiceWorker for local/native!',
];

/** How many page loads this log holds — see DOCUMENT_LOAD_MARKERS. */
function countDocumentLoads(log: string): number {
  // for each marker, add the pages it accounts for (one page prints exactly one of them)
  return DOCUMENT_LOAD_MARKERS.reduce((total, marker) => total + countOccurrences(log, marker), 0);
}

export type BootFreezeCategory =
  | 'abandoned-background' // benign: app backgrounded before boot completed
  | 'webview-no-js' // real hang: WebView created but JS never ran
  | 'js-exception' // real hang: an uncaught JS error escaped during boot
  | 'auth-loop' // real hang: a tight Firebase accounts:lookup retry storm
  | 'pre-webview' // real hang/crash: native onCreate started but the WebView was never created (O6, #4027 Step 24)
  | 'reload-loop' // real hang: the page kept loading over and over and the boot never finished (#4942)
  | 'unknown-incomplete'; // unexplained incomplete boot — report conservatively

export interface BootFreezeDecision {
  /**
   * Report under FreezeWatchdog:BootLog (a genuine or unexplained hang). When
   * false, the caller downgrades to the low-signal
   * FreezeWatchdog:BootAbandonedBackground key instead of polluting BootLog.
   */
  report: boolean;
  category: BootFreezeCategory;
}

/** matches the real uncaught-error console shape: "Uncaught (in promise)" or
 * "Uncaught <ErrorType>" — not the bare word in arbitrary prose */
const UNCAUGHT_ERROR_RE = /Uncaught (\(in promise\)|[A-Z])/;

/** count non-overlapping occurrences of needle in haystack */
function countOccurrences(haystack: string, needle: string): number {
  // if needle is empty, indexOf never advances — guard against an infinite loop
  if (!needle) return 0;
  let count = 0;
  let from = 0;
  // for each match, count it and resume the search past it
  for (;;) {
    const at = haystack.indexOf(needle, from);
    if (at < 0) return count;
    count++;
    from = at + needle.length;
  }
}

/**
 * The line iOS writes when the app leaves the foreground (#5179).
 *
 * Until it existed, a store build on iOS had NEITHER backgrounded signal — no
 * native lifecycle line, and the JS fallback below was a `console.log`, which a
 * production build drops before the native capture sees it. Every backgrounded
 * iOS boot was therefore reported as an unexplained hang. The counts, and what
 * they mean for reading this issue's history, are in `design-docs/telemetry.md`.
 *
 * MIRRORED TEXT: written by `applicationDidEnterBackground` in
 * `client/ios-overlay/App/App/AppDelegate.swift`. No test in this repo can run
 * Swift, so `boot-freeze-classifier.test.ts` reads that file and fails if the
 * string is not found in it — without which a rename there would leave this rule
 * matching nothing for ever with every test still green.
 *
 * Deliberately NOT the same string as Android's: a person reading a boot-freeze
 * attachment should be able to tell which platform wrote it.
 */
export const IOS_BACKGROUNDED_MARKER = 'Native: didEnterBackground (backgrounded)';

/**
 * Did the app leave the foreground during this boot?
 *
 * The native markers are the robust signal, and each platform writes its own:
 * Android `Native: onStop` (MainActivity.onStop, #3797), iOS
 * IOS_BACKGROUNDED_MARKER (AppDelegate.applicationDidEnterBackground, #5179).
 *
 * The JS "App: Focus off" line is a fallback for both. It covers only the window
 * after FocusStateService is constructed, so it can never replace a native
 * marker — and until #5179 it was written with console.log, which a production
 * build drops before the native capture sees it, making it dead code in every
 * store build on both platforms.
 */
function wasBackgrounded(log: string): boolean {
  return log.includes('Native: onStop')
    || log.includes(IOS_BACKGROUNDED_MARKER)
    || log.includes('App: Focus off');
}

/**
 * The Capacitor native bridge delivers lifecycle/state events to the WebView by
 * eval-ing `window.Capacitor.triggerEvent(...)` into the page (Bridge.java's
 * triggerJSEvent, fired by the App plugin's pause / appStateChange listeners).
 * When such an event fires before the WebView has executed Capacitor's runtime,
 * `window.Capacitor` is still undefined and the eval throws an uncaught
 * "reading 'triggerEvent'" TypeError. It is a benign, native-injected,
 * fire-and-forget artifact: it does NOT stop our separately-loading Angular
 * bundle, it is NOT real app JS output, and it is NOT an app exception. So it
 * must not mask the boot's true category (a benign abandon, or a "JS never ran"
 * renderer hang). #3906. We match both V8 phrasings — "Cannot read properties
 * of undefined (reading 'triggerEvent')" and the older "Cannot read property
 * 'triggerEvent' of undefined" — by requiring the uncaught-error shape plus the
 * 'triggerEvent' and 'undefined' words on the same line.
 */
function isCapacitorEarlyBootArtifact(line: string): boolean {
  return line.includes('triggerEvent') && line.includes('undefined') && UNCAUGHT_ERROR_RE.test(line);
}

/** did any REAL app JS console line run? a line counts only if it carries the
 * `[JS:` marker and is not the benign Capacitor early-boot triggerEvent artifact */
function hasRealAppJsLine(log: string): boolean {
  // for each log line, treat it as real app JS only if it is a [JS: line that is not the artifact
  return log.split('\n').some(line => line.includes('[JS:') && !isCapacitorEarlyBootArtifact(line));
}

/** is there an uncaught error that is a REAL app exception (i.e. an uncaught
 * error that is not the benign Capacitor early-boot triggerEvent artifact)? */
function hasNonArtifactUncaughtError(log: string): boolean {
  // for each log line, count it only if it is an uncaught error and not the artifact
  return log.split('\n').some(line => UNCAUGHT_ERROR_RE.test(line) && !isCapacitorEarlyBootArtifact(line));
}

export function classifyBootFreeze(log: string): BootFreezeDecision {
  // a backgrounded boot is the dominant benign false positive (Android kills
  // backgrounded processes aggressively). Compute it once: real-hang signatures
  // that imply JS actually ran (js-exception, auth-loop) still win over it
  // below, but the JS-less webview-no-js rule must defer to it — see that branch.
  const backgrounded = wasBackgrounded(log);

  // the WebView was created but no REAL app JS console line ever ran — our code
  // never ran (renderer-gone / OOM / WebView startup failure). but only when the
  // app stayed in the foreground: on a slow cold start the user often backgrounds
  // the app in the long WebView-ready-to-first-JS-line window, which also leaves
  // no real "[JS:" line — that is a benign abandon (handled below), not a
  // renderer hang. #3906: the Capacitor native triggerEvent artifact is
  // native-injected, not our bundle, so it does NOT count as "JS ran".
  if (log.includes('WebView ready') && !hasRealAppJsLine(log) && !backgrounded) {
    return { report: true, category: 'webview-no-js' };
  }

  // an uncaught APP JS error escaped during boot. #3906: ignore logs whose only
  // uncaught error is the benign Capacitor early-boot triggerEvent artifact —
  // that is a framework artifact, not an app exception, and must not be reported
  // as one (it would mask the boot's true category: benign abandon, or the
  // webview-no-js renderer hang above).
  if (hasNonArtifactUncaughtError(log)) {
    return { report: true, category: 'js-exception' };
  }

  // a tight loop of Firebase auth lookups never let boot finish
  if (countOccurrences(log, 'accounts:lookup') >= AUTH_LOOP_MIN_OCCURRENCES) {
    return { report: true, category: 'auth-loop' };
  }

  // #4942: the page kept loading over and over and the boot never finished. The
  // customer sees a frozen app whose splash flashes; the server sees one login
  // per page load. Placed here deliberately, between two rules it must not
  // disturb:
  //
  //  - AFTER js-exception and auth-loop, because both of those say something
  //    about WHY a boot failed, and this rule only says what the failure looked
  //    like. A storm with an uncaught app error in it is still best filed under
  //    the error.
  //  - BEFORE the backgrounded downgrade, because a storm the user finally got
  //    tired of and backgrounded is still a storm. #3797's precedence is right
  //    for a boot that was merely interrupted; a log that already shows ten or
  //    more page loads is not that, and downgrading it would drop the one
  //    signature that names this defect.
  if (countDocumentLoads(log) >= RELOAD_LOOP_MIN_DOCUMENT_LOADS) {
    return { report: true, category: 'reload-loop' };
  }

  // #3797: the app was sent to the background before boot completed — a benign
  // interrupted boot, not a hang. Downgrade it to the low-signal key instead of
  // polluting FreezeWatchdog:BootLog.
  if (backgrounded) {
    return { report: false, category: 'abandoned-background' };
  }

  // O6 (#4027 Step 24): native onCreate started but the "Native: WebView ready"
  // marker never followed — the process died inside native init, before (or
  // while) creating the WebView. These used to hide in unknown-incomplete;
  // their own signature makes the pre-WebView cohort countable. Both markers
  // are written by Android's MainActivity.onCreate and, since #5360, by iOS's
  // MainViewController (instanceDescriptor and capacitorDidLoad). Placed after
  // the backgrounded downgrade on purpose: an onStop marker implies onCreate
  // returned (so the ready marker should exist), making overlap ~impossible,
  // and #3797's benign-abandon precedence stays intact if it ever happens.
  if (log.includes('Native: onCreate') && !log.includes('WebView ready')) {
    return { report: true, category: 'pre-webview' };
  }

  // unexplained incomplete boot — report conservatively, never silently drop
  return { report: true, category: 'unknown-incomplete' };
}
